import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AuditItem } from '@/lib/supabase';
import { useUpdateAuditItem } from '@/hooks/useAuditItems';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Zap,
  ExternalLink,
} from 'lucide-react';

interface AuditItemsListProps {
  items: AuditItem[];
  title: string;
  category: 'technique' | 'semantique' | 'local';
}

export const AuditItemsList: React.FC<AuditItemsListProps> = ({
  items,
  title,
  category,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const updateMutation = useUpdateAuditItem();

  const handleToggleStatus = async (item: AuditItem) => {
    const newStatus = item.status === 'completed' ? 'todo' : 'completed';
    await updateMutation.mutateAsync({
      id: item.id,
      updates: {
        status: newStatus,
        completed_date: newStatus === 'completed' ? new Date().toISOString() : null,
      },
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-blue-600" />;
      case 'blocked':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Circle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, any> = {
      P0: { variant: 'destructive', text: 'P0 - Critique' },
      P1: { variant: 'warning', text: 'P1 - Important' },
      P2: { variant: 'default', text: 'P2 - Nice to have' },
    };
    const config = variants[priority] || variants.P2;
    return (
      <Badge variant={config.variant} className="text-xs">
        {config.text}
      </Badge>
    );
  };

  const getCategoryColor = (cat: string) => {
    const colors: Record<string, string> = {
      technique: 'bg-blue-50 border-blue-200',
      semantique: 'bg-purple-50 border-purple-200',
      local: 'bg-green-50 border-green-200',
    };
    return colors[cat] || colors.technique;
  };

  const completedCount = items.filter(i => i.status === 'completed').length;
  const totalCount = items.length;
  const completionRate = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`border rounded-lg overflow-hidden ${getCategoryColor(category)}`}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/50 transition"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-600" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-600" />
          )}
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <Badge variant="outline">
            {completedCount}/{totalCount}
          </Badge>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            <span className="font-medium">{completionRate}%</span> complété
          </div>
          <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-600 transition-all duration-300"
              style={{ width: `${completionRate}%` }}
            />
          </div>
        </div>
      </div>

      {/* Items */}
      {isExpanded && (
        <div className="bg-white border-t">
          {items.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Aucun item d'audit dans cette catégorie
            </div>
          ) : (
            <div className="divide-y">
              {items.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`p-4 hover:bg-gray-50 transition ${
                    item.status === 'completed' ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <button
                      onClick={() => handleToggleStatus(item)}
                      disabled={updateMutation.isPending}
                      className="mt-1 hover:scale-110 transition disabled:opacity-50"
                    >
                      {getStatusIcon(item.status)}
                    </button>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h4
                            className={`font-medium text-gray-900 mb-1 ${
                              item.status === 'completed' ? 'line-through' : ''
                            }`}
                          >
                            {item.title}
                          </h4>
                          {item.description && (
                            <p className="text-sm text-gray-600 mb-2">
                              {item.description}
                            </p>
                          )}
                          {item.url && (
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              En savoir plus
                            </a>
                          )}
                        </div>

                        {/* Badges */}
                        <div className="flex flex-col items-end gap-2">
                          {getPriorityBadge(item.priority)}
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Zap className="w-3 h-3" />
                            ICE: {item.ice_score.toFixed(1)}
                          </div>
                        </div>
                      </div>

                      {/* Metadata */}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        {item.subcategory && (
                          <span className="bg-gray-100 px-2 py-1 rounded">
                            {item.subcategory}
                          </span>
                        )}
                        {item.assignee && (
                          <span>Assigné à : {item.assignee}</span>
                        )}
                        {item.due_date && (
                          <span>
                            Échéance :{' '}
                            {new Date(item.due_date).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                        {item.completed_date && (
                          <span className="text-green-600">
                            ✓ Complété le{' '}
                            {new Date(item.completed_date).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                      </div>

                      {/* Notes */}
                      {item.notes && (
                        <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                          📝 {item.notes}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};


